// Trends: what's changing week on week, and what to do about it. Daily
// series for the charts, headline metrics with last week alongside, and
// "smart insights" — rules that look for the patterns that actually help
// (what sets off accidents, whether he's holding on longer, how the nights
// are going). Each insight says where it comes from; none fire without
// enough data behind them. The AI's daily insights (server/brief.ts) start
// from these numbers.

import { BEHAVIOURS } from '../content/behaviour.ts';
import { FIGURES } from '../content/sources.ts';
import type { SourceRef } from '../content/types.ts';
import { awakeWindowMinutes, sleepTargetHours, toiletIntervalMinutes } from './age.ts';
import { behaviourPattern } from './coach.ts';
import { awakeStretches, sleepDay, sleepIntervals, sleepNight, type NightWindow } from './sleep.ts';
import { DAY, HOUR, MINUTE, dayStart, formatDuration } from './time.ts';
import type { LogEvent, Millis } from './types.ts';

export type MetricId = 'outside' | 'accidents' | 'holding' | 'sleep' | 'night' | 'wakes' | 'awake' | 'training' | 'social' | 'spells';

export interface Metric {
  id: MetricId;
  label: string;
  /** This week (last 7 days) and the week before; undefined = not enough logged. */
  value?: number;
  prev?: number;
  /** One value per day, oldest first (undefined = nothing to go on that day). */
  series: (number | undefined)[];
  unit: '%' | 'h' | 'min' | '';
  /** Which way is good. */
  better: 'up' | 'down';
  from: 'logs' | 'camera';
}

/** Better, worse or steady against the week before (undefined: nothing to compare). */
export function metricChange(m: Metric): 'better' | 'worse' | 'steady' | undefined {
  if (m.value === undefined || m.prev === undefined) return undefined;
  const d = m.value - m.prev;
  const small = m.unit === '%' ? 3 : m.unit === 'h' ? 0.25 : m.unit === 'min' ? 3 : 0.5;
  if (Math.abs(d) < small) return 'steady';
  return d > 0 === (m.better === 'up') ? 'better' : 'worse';
}

export type InsightKind = 'win' | 'tip' | 'watch';

export interface TrendInsight {
  id: string;
  kind: InsightKind;
  title: string;
  detail: string;
  from: 'logs' | 'camera';
  sources?: SourceRef[];
  route?: string;
  /** Higher first. */
  score: number;
}

export interface Trends {
  /** Midnight of each day in `series`, oldest first. */
  days: Millis[];
  metrics: Metric[];
  insights: TrendInsight[];
}

const median = (xs: number[]) => {
  if (!xs.length) return undefined;
  const s = [...xs].sort((a, b) => a - b);
  return s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
};
const mean = (xs: (number | undefined)[]) => {
  const v = xs.filter((x): x is number => x !== undefined);
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : undefined;
};
const sum = (xs: (number | undefined)[]) => xs.reduce<number>((a, b) => a + (b ?? 0), 0);
const isToilet = (e: LogEvent): e is LogEvent<'wee' | 'poo'> => e.type === 'wee' || e.type === 'poo';
const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;

export function trends(events: LogEvent[], now: Millis, ageWeeks: number, night: NightWindow, span = 14): Trends {
  const days = Array.from({ length: span }, (_, i) => dayStart(now, i - span + 1));
  const inDay = (d: Millis) => (e: LogEvent) => e.at >= d && e.at < dayStart(d, 1) && e.at <= now;
  const week = (i: number) => i >= span - 7;
  const toilets = events.filter(isToilet).filter((e) => e.at <= now).sort((a, b) => a.at - b.at);

  // --- Daily series -----------------------------------------------------------------
  const outside: (number | undefined)[] = [];
  const accidents: (number | undefined)[] = [];
  const holding: (number | undefined)[] = [];
  const sleep: (number | undefined)[] = [];
  const nights: (number | undefined)[] = [];
  const wakes: (number | undefined)[] = [];
  const awake: (number | undefined)[] = [];
  const training: (number | undefined)[] = [];
  const social: (number | undefined)[] = [];
  const spells: (number | undefined)[] = [];
  const longest: (number | undefined)[] = [];
  for (const d of days) {
    const t = toilets.filter(inDay(d));
    const out = t.filter((e) => e.data.where === 'outside').length;
    outside.push(t.length ? (out / t.length) * 100 : undefined);
    accidents.push(t.length ? t.length - out : undefined);
    holding.push(median(gapsAwake(t, events)));
    const sd = sleepDay(events, d, now, night);
    sleep.push(sd.wellLogged ? sd.totalMs / HOUR : undefined);
    const n = sleepNight(events, d, now, night);
    nights.push(n.logged && n.sleepMs > 3 * HOUR ? n.sleepMs / HOUR : undefined);
    wakes.push(n.logged && n.sleepMs > 3 * HOUR ? n.wakes : undefined);
    longest.push(n.logged && n.sleepMs > 3 * HOUR ? n.longestMs / HOUR : undefined);
    awake.push(median(awakeStretches(events, d, Math.min(dayStart(d, 1), now), night)));
    const day = events.filter(inDay(d));
    const any = day.length > 0;
    training.push(any ? day.filter((e) => e.type === 'training').length : undefined);
    social.push(any ? day.filter((e) => e.type === 'social').length : undefined);
    spells.push(any ? day.filter((e) => e.type === 'behaviour').length : undefined);
  }

  const split = <T,>(xs: T[]) => [xs.filter((_, i) => !week(i)), xs.filter((_, i) => week(i))] as const;
  const pct = (list: LogEvent<'wee' | 'poo'>[]) => (list.length ? (list.filter((e) => e.data.where === 'outside').length / list.length) * 100 : undefined);
  const weekAgo = now - 7 * DAY;
  const twoWeeks = now - 14 * DAY;
  const thisWeekToilets = toilets.filter((e) => e.at > weekAgo);
  const lastWeekToilets = toilets.filter((e) => e.at > twoWeeks && e.at <= weekAgo);
  const avgOf = (xs: (number | undefined)[]) => {
    const [p, c] = split(xs);
    return { value: mean(c), prev: mean(p) };
  };
  const totalOf = (xs: (number | undefined)[]) => {
    const [p, c] = split(xs);
    return { value: c.some((x) => x !== undefined) ? sum(c) : undefined, prev: p.some((x) => x !== undefined) ? sum(p) : undefined };
  };

  const metrics: Metric[] = [
    { id: 'outside', label: 'Toilet trips outside', value: pct(thisWeekToilets), prev: pct(lastWeekToilets), series: outside, unit: '%', better: 'up', from: 'logs' },
    { id: 'accidents', label: 'Accidents indoors', ...totalOf(accidents), series: accidents, unit: '', better: 'down', from: 'logs' },
    { id: 'holding', label: 'Holds it for', ...avgOf(holding), series: holding, unit: 'min', better: 'up', from: 'logs' },
    { id: 'sleep', label: 'Sleep a day', ...avgOf(sleep), series: sleep, unit: 'h', better: 'up', from: 'logs' },
    { id: 'night', label: 'Night sleep', ...avgOf(nights), series: nights, unit: 'h', better: 'up', from: 'logs' },
    { id: 'wakes', label: 'Night wakes', ...avgOf(wakes), series: wakes, unit: '', better: 'down', from: 'logs' },
    { id: 'awake', label: 'Awake between naps', ...avgOf(awake), series: awake, unit: 'min', better: 'down', from: 'logs' },
    { id: 'training', label: 'Training sessions', ...totalOf(training), series: training, unit: '', better: 'up', from: 'logs' },
    { id: 'social', label: 'New experiences', ...totalOf(social), series: social, unit: '', better: 'up', from: 'logs' },
    { id: 'spells', label: 'Wild or tricky spells', ...totalOf(spells), series: spells, unit: '', better: 'down', from: 'logs' },
  ];
  const m = (id: MetricId) => metrics.find((x) => x.id === id)!;

  // --- Smart insights ---------------------------------------------------------------
  const out: TrendInsight[] = [];
  const add = (i: TrendInsight) => out.push(i);

  // What sets accidents off (last 14 days).
  const recentAccidents = toilets.filter((e) => e.data.where === 'inside' && e.at > twoWeeks);
  if (recentAccidents.length >= 2) {
    const interval = toiletIntervalMinutes(ageWeeks) * MINUTE;
    const counts = { woke: 0, meal: 0, play: 0, gap: 0, other: 0 };
    for (const a of recentAccidents) counts[accidentTrigger(a, events, toilets, interval)]++;
    const [top, n] = (Object.entries(counts) as [keyof typeof counts, number][]).sort((a, b) => b[1] - a[1])[0];
    if (top !== 'other' && n >= 2 && n / recentAccidents.length >= 0.5) {
      const what = { woke: 'within 20 minutes of waking up', meal: 'within 30 minutes of a meal', play: 'straight after play or training', gap: `more than ${Math.round(interval / MINUTE)} minutes after his last trip out` }[top];
      const fix = {
        woke: 'Carry him straight out the moment he stirs — before the wee, not after.',
        meal: 'Head out 10–15 minutes after every meal, even if he went just before.',
        play: 'Build a toilet trip into the end of every game or session.',
        gap: 'Shorten the gaps a little for now — set the trip earlier than you think.',
      }[top];
      add({ id: 'accident-trigger', kind: 'tip', title: `${n} of ${recentAccidents.length} accidents came ${what}`, detail: fix, from: 'logs', sources: [...FIGURES.toiletInterval.sources], route: '/insights', score: 90 + n });
    }
    // When in the day.
    const byHour = new Map<number, number>();
    for (const a of recentAccidents) {
      const h = new Date(a.at).getHours();
      byHour.set(h, (byHour.get(h) ?? 0) + 1);
    }
    const [hour, hn] = [...byHour.entries()].map(([h]) => [h, (byHour.get(h) ?? 0) + (byHour.get((h + 1) % 24) ?? 0)] as const).sort((a, b) => b[1] - a[1])[0];
    if (hn >= 2 && hn / recentAccidents.length >= 0.5) add({ id: 'accident-hour', kind: 'tip', title: `Accidents cluster ${hh(hour)}–${hh((hour + 2) % 24)}`, detail: `${hn} of ${recentAccidents.length} indoor accidents were in that window. Add a trip out just before ${hh(hour)}.`, from: 'logs', route: '/insights', score: 70 + hn });
  }

  // Toilet success, week on week.
  const ok = m('outside');
  if (ok.value !== undefined && ok.prev !== undefined && thisWeekToilets.length >= 8 && lastWeekToilets.length >= 8) {
    const d = ok.value - ok.prev;
    if (d >= 8) add({ id: 'outside-up', kind: 'win', title: `${Math.round(ok.value)}% of toilet trips outside — up from ${Math.round(ok.prev)}%`, detail: 'The routine is working. Keep rewarding every wee outside within a couple of seconds.', from: 'logs', score: 60 + d });
    else if (d <= -8) add({ id: 'outside-down', kind: 'watch', title: `Fewer trips outside: ${Math.round(ok.value)}%, down from ${Math.round(ok.prev)}%`, detail: 'Setbacks are normal (teething, a change of routine). Go back to more frequent trips for a few days. A sudden rise in wees or accidents can be a urine infection — mention it to the vet.', from: 'logs', sources: [...FIGURES.toiletInterval.sources], score: 85 - d });
  }

  // Holding on longer.
  const hold = m('holding');
  if (hold.value !== undefined && hold.prev !== undefined && hold.value - hold.prev >= 8 && hold.value / hold.prev >= 1.15)
    add({ id: 'holding-up', kind: 'win', title: `Holding on longer: about ${Math.round(hold.value)} min between trips`, detail: `Up from about ${Math.round(hold.prev)} minutes the week before (awake time only). Stretch the routine gradually — not overnight.`, from: 'logs', score: 55 });

  // Accident-free streak.
  const lastAccident = [...toilets].reverse().find((e) => e.data.where === 'inside');
  if (lastAccident && thisWeekToilets.length >= 6 && now - lastAccident.at >= DAY)
    add({ id: 'clean-streak', kind: 'win', title: `${formatDuration(now - lastAccident.at)} without an accident`, detail: 'A great streak — no need to change anything.', from: 'logs', score: 50 + Math.min(20, (now - lastAccident.at) / DAY) });

  // Sleep.
  const [lo, hi] = sleepTargetHours(ageWeeks);
  const sl = m('sleep');
  if (sl.value !== undefined && sl.series.filter((x, i) => week(i) && x !== undefined).length >= 2) {
    if (sl.value < lo - 2) add({ id: 'sleep-low', kind: 'watch', title: `About ${Math.round(sl.value)} h of sleep a day logged`, detail: `Guidance for his age is ${lo}–${hi} hours. Most bitey, wild spells are tiredness: about an hour awake, then a nap in his crate with a chew.`, from: 'logs', sources: [...FIGURES.sleepHours.sources], route: '/training/help?b=wont-settle', score: 80 });
    else if (sl.value >= lo - 1) add({ id: 'sleep-ok', kind: 'win', title: `Sleeping well: about ${Math.round(sl.value)} h a day`, detail: `Right where it should be for his age (${lo}–${hi} h).`, from: 'logs', sources: [...FIGURES.sleepHours.sources], score: 30 });
  }

  // Nights.
  const nightLong = mean(longest.filter((_, i) => week(i)));
  const nightLongPrev = mean(longest.filter((_, i) => !week(i)));
  if (nightLong !== undefined && nightLongPrev !== undefined && nightLong - nightLongPrev >= 0.5)
    add({ id: 'night-longer', kind: 'win', title: `Longer nights: best stretch about ${formatDuration(nightLong * HOUR)}`, detail: `Up from about ${formatDuration(nightLongPrev * HOUR)} the week before. Keep night trips boring — lights low, no chat, straight back to bed.`, from: 'logs', score: 58 });
  const wk = m('wakes');
  if (wk.value !== undefined && wk.prev !== undefined && wk.prev - wk.value >= 0.5) add({ id: 'wakes-down', kind: 'win', title: `Fewer night wakes: about ${wk.value.toFixed(1)} a night`, detail: `Down from ${wk.prev.toFixed(1)}.`, from: 'logs', score: 52 });

  // Awake too long between naps.
  const aw = m('awake');
  const window = awakeWindowMinutes(ageWeeks);
  if (aw.value !== undefined && aw.series.filter((x, i) => week(i) && x !== undefined).length >= 2 && aw.value > window * 1.25)
    add({ id: 'awake-long', kind: 'tip', title: `Often up for about ${Math.round(aw.value)} min between naps`, detail: `At ${Math.floor(ageWeeks)} weeks, about ${window} minutes is plenty. Start the wind-down a little earlier — it heads off most wild spells.`, from: 'logs', sources: [...FIGURES.awakeWindow.sources], route: '/training/help?b=wont-settle', score: 75 });

  // Behaviour patterns: the most-logged spell this week, with when it happens.
  const spellEvents = events.filter((e): e is LogEvent<'behaviour'> => e.type === 'behaviour' && e.at > weekAgo && e.at <= now);
  const kinds = [...new Set(spellEvents.map((e) => e.data.kind))];
  const topKind = kinds.sort((a, b) => spellEvents.filter((e) => e.data.kind === b).length - spellEvents.filter((e) => e.data.kind === a).length)[0];
  const pattern = topKind ? behaviourPattern(topKind, events, now, ageWeeks) : undefined;
  if (topKind && pattern && (pattern.awakeMinutes !== undefined || pattern.window)) {
    const b = BEHAVIOURS.find((x) => x.id === topKind);
    add({ id: `pattern-${topKind}`, kind: 'tip', title: `${b?.label ?? topKind}: ${pattern.count} spells — there’s a pattern`, detail: pattern.advice.charAt(0).toUpperCase() + pattern.advice.slice(1), from: 'logs', sources: b?.sources, route: `/training/help?b=${topKind}`, score: 78 });
  }
  const sp = m('spells');
  if (sp.value !== undefined && sp.prev !== undefined && sp.prev >= 4 && sp.value <= sp.prev * 0.6) add({ id: 'spells-down', kind: 'win', title: `Fewer wild spells: ${sp.value}, down from ${sp.prev}`, detail: 'Whatever you changed is working — usually it’s the naps.', from: 'logs', score: 56 });

  // Appetite.
  const recentMeals = events.filter((e): e is LogEvent<'meal'> => e.type === 'meal' && e.at > now - 3 * DAY && e.at <= now);
  const offFood = recentMeals.filter((e) => e.data.appetite === 'some' || e.data.appetite === 'none').length;
  if (offFood >= 2) add({ id: 'appetite', kind: 'watch', title: `${offFood} meals not finished in 3 days`, detail: 'Teething can put puppies off food for a day or two. If he’s also quiet, sick or has an upset tummy, call the vet.', from: 'logs', route: '/food', score: 82 });

  // Training rhythm.
  const tr = m('training');
  const lastTraining = events.filter((e) => e.type === 'training' && e.at <= now).reduce((t, e) => Math.max(t, e.at), 0);
  if (events.some((e) => e.at > now - 3 * DAY) && now - lastTraining > 2 * DAY) add({ id: 'training-gap', kind: 'tip', title: 'No training logged for a couple of days', detail: 'Two minutes before a meal is plenty — little and often beats long sessions.', from: 'logs', sources: [...FIGURES.rewardBased.sources], route: '/training', score: 48 });
  else if (tr.value !== undefined && tr.value >= 14) add({ id: 'training-good', kind: 'win', title: `${tr.value} training sessions this week`, detail: tr.prev !== undefined && tr.prev < tr.value ? `Up from ${tr.prev}. Lovely consistency.` : 'Lovely consistency.', from: 'logs', score: 40 });

  // Socialisation window.
  const so = m('social');
  if (ageWeeks < 16 && (so.value ?? 0) < 5 && events.some((e) => e.at > weekAgo)) add({ id: 'social-low', kind: 'tip', title: `${so.value ?? 0} new experiences this week`, detail: `About ${16 - Math.floor(ageWeeks)} weeks left of the key socialisation window. One or two short, happy new things a day — watching from your arms counts.`, from: 'logs', sources: [...FIGURES.socialWindow.sources], route: '/social', score: 66 });
  else if ((so.value ?? 0) >= 10) add({ id: 'social-good', kind: 'win', title: `${so.value} new experiences this week`, detail: 'Brilliant socialising. Keep it at his pace.', from: 'logs', score: 42 });

  // Barking (camera + logs).
  const barks = events.filter((e) => ((e.type === 'camera' && e.data.kind === 'bark') || (e.type === 'behaviour' && e.data.kind === 'barking')) && e.at > weekAgo && e.at <= now);
  if (barks.length >= 3) {
    const cam = barks.some((e) => e.type === 'camera');
    add({ id: 'barking', kind: 'watch', title: `${barks.length} barking episodes this week`, detail: 'Note what set each one off — noise, the door, being left. Reward quiet, and make flat noises boring early.', from: cam ? 'camera' : 'logs', sources: BEHAVIOURS.find((b) => b.id === 'barking')?.sources, route: '/training/help?b=barking', score: 72 });
  }

  return { days, metrics, insights: out.sort((a, b) => b.score - a.score) };
}

/** Minutes between consecutive toilet trips in a day, skipping gaps with a nap in them. */
function gapsAwake(toilets: LogEvent<'wee' | 'poo'>[], events: LogEvent[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < toilets.length; i++) {
    const a = toilets[i - 1].at;
    const b = toilets[i].at;
    if (b - a < 5 * MINUTE) continue; // wee and poo on the same trip
    const slept = sleepIntervals(events, a, b).reduce((n, [s, t]) => n + (t - s), 0);
    if (slept > 15 * MINUTE) continue;
    out.push((b - a) / MINUTE);
  }
  return out;
}

/** What most likely set off an indoor accident. */
function accidentTrigger(a: LogEvent, events: LogEvent[], toilets: LogEvent<'wee' | 'poo'>[], interval: Millis): 'woke' | 'meal' | 'play' | 'gap' | 'other' {
  const woke = events.some((e) => e.type === 'sleep' && e.end !== undefined && e.end <= a.at && a.at - e.end <= 20 * MINUTE);
  if (woke) return 'woke';
  if (events.some((e) => e.type === 'meal' && e.at <= a.at && a.at - e.at <= 30 * MINUTE)) return 'meal';
  if (events.some((e) => (e.type === 'play' || e.type === 'training' || e.type === 'walk') && (e.end ?? e.at) <= a.at && a.at - (e.end ?? e.at) <= 15 * MINUTE)) return 'play';
  const prev = [...toilets].reverse().find((e) => e.at < a.at - MINUTE);
  return prev && a.at - prev.at >= interval ? 'gap' : 'other';
}
